// ══════════════════════════════════════════════════════
// Trade Table (Phase 2c)
// Sortier-/filterbare Tabelle für Trades mit Pagination + CSV-Export.
// Wiederverwendet SortTh, sortRows, downloadCSV aus components/shared.jsx
// für konsistenten Stil zur bestehenden BtDetail-Trade-Tabelle.
// ══════════════════════════════════════════════════════

import { useState, useMemo } from "react";
import { theme, fonts } from "../theme";
import { Pill, Button } from "./ui";
import { SortTh, sortRows, downloadCSV } from "./shared";
import { formatPnl } from "./ui";
import TradeVisualizer from "./TradeVisualizer";

const PAGE_SIZE = 50;

// ─── Helper: compact dollar ─────────────────────
const dollar = (v, d = 0) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 10000) return `${n < 0 ? "-" : ""}$${(abs / 1000).toFixed(1)}k`;
  return `${n < 0 ? "-" : ""}$${abs.toFixed(d)}`;
};

// ─── Filter-Toggle-Buttons ──────────────────────
function ToggleGroup({ options, value, onChange, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {label && (
        <span style={{ fontSize: 10, color: theme.text3, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.3px" }}>
          {label}
        </span>
      )}
      <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 6,
                    background: theme.tint }}>
        {options.map(opt => (
          <button key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer",
              fontSize: 10, fontWeight: value === opt.value ? 700 : 500,
              background: value === opt.value ? theme.white : "transparent",
              color: value === opt.value ? theme.text : theme.text3,
              boxShadow: value === opt.value ? theme.shadow1 : "none",
              fontFamily: fonts.body, textTransform: "capitalize",
            }}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Exit-Reason-Multi-Toggle (Pills) ───────────
function ReasonToggles({ allReasons, selected, onChange }) {
  if (!allReasons || allReasons.length === 0) return null;
  const toggle = r => {
    if (selected.includes(r)) onChange(selected.filter(x => x !== r));
    else onChange([...selected, r]);
  };
  const allSelected = selected.length === 0 || selected.length === allReasons.length;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, color: theme.text3, fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.3px" }}>
        Reason:
      </span>
      <button
        onClick={() => onChange([])}
        style={{
          padding: "3px 9px", borderRadius: 10, border: `1px solid ${theme.border}`,
          cursor: "pointer", fontSize: 10,
          background: allSelected ? theme.blue : theme.white,
          color: allSelected ? "#fff" : theme.text3,
          fontFamily: fonts.mono, fontWeight: 600,
        }}>
        ALL
      </button>
      {allReasons.map(r => {
        const active = selected.includes(r);
        return (
          <button key={r}
            onClick={() => toggle(r)}
            style={{
              padding: "3px 9px", borderRadius: 10,
              border: `1px solid ${active ? theme.blueText : theme.border}`,
              cursor: "pointer", fontSize: 10,
              background: active ? theme.blueLight : theme.white,
              color: active ? theme.blueText : theme.text3,
              fontFamily: fonts.mono, fontWeight: 600,
            }}>
            {r}
          </button>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// MAIN: TradeTable
// Props:
//   trades: Liste der Trade-Dicts (Phase-1a-Schema)
//   filename: String fürs CSV (ohne .csv)
//   meta: optional — String für Info-Zeile (z.B. "Trial #44" oder "Window 2 OOS")
// ═════════════════════════════════════════════════════════
export default function TradeTable({ trades, filename = "trades", meta, vizContext }) {
  const [sort, setSort] = useState({ key: null, asc: false });
  const [page, setPage] = useState(0);
  const [directionFilter, setDirectionFilter] = useState("all");
  const [pnlFilter, setPnlFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState([]);
  const [vizState, setVizState] = useState({ open: false, index: 0 });

  // Alle verfügbaren Exit-Reasons aus Daten ableiten
  const allReasons = useMemo(() => {
    const set = new Set();
    (trades || []).forEach(t => { if (t.exit_reason) set.add(t.exit_reason); });
    return Array.from(set).sort();
  }, [trades]);

  // Filter anwenden
  const filtered = useMemo(() => {
    if (!trades) return [];
    return trades.filter(t => {
      if (directionFilter !== "all" && t.direction !== directionFilter) return false;
      if (pnlFilter === "winners" && !((t.pnl || 0) > 0.01)) return false;
      if (pnlFilter === "losers" && !((t.pnl || 0) < -0.01)) return false;
      if (reasonFilter.length > 0 && !reasonFilter.includes(t.exit_reason)) return false;
      return true;
    });
  }, [trades, directionFilter, pnlFilter, reasonFilter]);

  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const slice = sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Wenn Filter wechselt → auf Page 0 zurück
  const resetPage = () => { if (page !== 0) setPage(0); };

  if (!trades || trades.length === 0) {
    return <div style={{ padding: 16, textAlign: "center",
                          color: theme.text4, fontSize: 12, fontStyle: "italic" }}>
      Keine Trade-Daten verfügbar
    </div>;
  }

  return (
    <div>
      {/* ── Filter-Zeile ── */}
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
                    marginBottom: 10, padding: "8px 10px", background: theme.tint,
                    borderRadius: 8 }}>
        <ToggleGroup
          label="Dir:"
          options={[
            { value: "all",   label: "Alle" },
            { value: "long",  label: "Long" },
            { value: "short", label: "Short" },
          ]}
          value={directionFilter}
          onChange={v => { setDirectionFilter(v); resetPage(); }}
        />
        <ToggleGroup
          label="P&L:"
          options={[
            { value: "all",     label: "Alle" },
            { value: "winners", label: "Winners" },
            { value: "losers",  label: "Losers" },
          ]}
          value={pnlFilter}
          onChange={v => { setPnlFilter(v); resetPage(); }}
        />
        {allReasons.length > 1 && (
          <ReasonToggles
            allReasons={allReasons}
            selected={reasonFilter}
            onChange={v => { setReasonFilter(v); resetPage(); }}
          />
        )}
      </div>

      {/* ── Count + Export-Button ── */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono }}>
          {filtered.length === trades.length
            ? `${trades.length} Trades`
            : `${filtered.length} von ${trades.length} Trades (gefiltert)`}
          {meta && <span style={{ color: theme.text4, marginLeft: 8 }}>· {meta}</span>}
        </div>
        <Button
          onClick={() => downloadCSV(sorted, `${filename}.csv`)}
          disabled={sorted.length === 0}>
          CSV Export ({sorted.length})
        </Button>
      </div>

      {/* ── Tabelle ── */}
      <div style={{ maxHeight: 520, overflow: "auto", border: `1px solid ${theme.border}`,
                    borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.divider}`,
                         position: "sticky", top: 0, background: theme.white, zIndex: 1 }}>
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700,
                           color: theme.text4, textTransform: "uppercase",
                           textAlign: "left" }}>#</th>
              <SortTh sortKey="entry_time" sort={sort} setSort={setSort}>Entry</SortTh>
              <SortTh sortKey="exit_time" sort={sort} setSort={setSort}>Exit</SortTh>
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700,
                           color: theme.text4, textTransform: "uppercase",
                           textAlign: "left" }}>Dir</th>
              <SortTh sortKey="entry_price" sort={sort} setSort={setSort}>Entry $</SortTh>
              <SortTh sortKey="exit_price" sort={sort} setSort={setSort}>Exit $</SortTh>
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700,
                           color: theme.text4, textTransform: "uppercase",
                           textAlign: "right" }}>Qty</th>
              <SortTh sortKey="pnl" sort={sort} setSort={setSort}>P&L</SortTh>
              <SortTh sortKey="pnl_pct" sort={sort} setSort={setSort}>P&L %</SortTh>
              <SortTh sortKey="mae" sort={sort} setSort={setSort}>MAE</SortTh>
              <SortTh sortKey="mfe" sort={sort} setSort={setSort}>MFE</SortTh>
              <th style={{ padding: "7px 10px", fontSize: 9, fontWeight: 700,
                           color: theme.text4, textTransform: "uppercase",
                           textAlign: "left" }}>Reason</th>
              {vizContext && <th style={{ padding: "7px 6px", width: 34 }} />}
            </tr>
          </thead>
          <tbody>
            {slice.map((t, i) => {
              const pnlPos = (t.pnl || 0) >= 0;
              const rowNum = safePage * PAGE_SIZE + i + 1;
              return (
                <tr key={t.trade_id ?? rowNum} className="sl-row"
                    style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <td style={{ padding: "5px 10px", fontSize: 10, color: theme.text4,
                               fontFamily: fonts.mono }}>
                    {t.trade_id ?? rowNum}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono }}>
                    {t.entry_time}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono }}>
                    {t.exit_time}
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <Pill bg={t.direction === "long" ? theme.greenLight : theme.redLight}
                          color={t.direction === "long" ? theme.greenText : theme.redText}>
                      {t.direction}
                    </Pill>
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono }}>
                    {Number(t.entry_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono }}>
                    {Number(t.exit_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10.5, fontFamily: fonts.mono,
                               textAlign: "right" }}>
                    {t.qty}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700,
                               fontFamily: fonts.mono,
                               color: pnlPos ? theme.greenText : theme.redText }}>
                    {formatPnl(t.pnl)}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                               color: pnlPos ? theme.greenText : theme.redText }}>
                    {t.pnl_pct != null ? `${Number(t.pnl_pct).toFixed(2)}%` : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                               color: theme.redText }}>
                    {t.mae != null ? dollar(t.mae, 0) : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                               color: theme.greenText }}>
                    {t.mfe != null ? dollar(t.mfe, 0) : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", fontSize: 10, fontFamily: fonts.mono,
                               color: theme.text3 }}>
                    {t.exit_reason}
                  </td>
                  {vizContext && (
                    <td style={{ padding: "5px 8px" }}>
                      <button
                        onClick={() => {
                          const absIdx = safePage * PAGE_SIZE + i;
                          setVizState({ open: true, index: absIdx });
                        }}
                        title="Chart anzeigen (Multi-TF Candlestick)"
                        style={{
                          padding: "4px 10px", borderRadius: 6,
                          border: `1px solid rgba(37,99,235,0.25)`,
                          background: theme.blueLight, cursor: "pointer",
                          fontSize: 12, display: "flex", gap: 4,
                          alignItems: "center", justifyContent: "center",
                          color: theme.blueText, fontWeight: 600,
                          whiteSpace: "nowrap", transition: "all 0.12s",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = theme.blueText;
                          e.currentTarget.style.color = "#fff";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = theme.blueLight;
                          e.currentTarget.style.color = theme.blueText;
                        }}
                      >
                        📈 Chart
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {slice.length === 0 && (
              <tr>
                <td colSpan={12} style={{ padding: "20px", textAlign: "center",
                                            color: theme.text4, fontSize: 12,
                                            fontStyle: "italic" }}>
                  Keine Trades nach Filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pageCount > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center",
                      gap: 8, padding: "10px 0" }}>
          <Button onClick={() => setPage(0)} disabled={safePage === 0}>« First</Button>
          <Button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}>
            ‹ Prev
          </Button>
          <span style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.mono,
                          minWidth: 110, textAlign: "center" }}>
            Page {safePage + 1} of {pageCount}
          </span>
          <Button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}>
            Next ›
          </Button>
          <Button onClick={() => setPage(pageCount - 1)}
                  disabled={safePage >= pageCount - 1}>
            Last »
          </Button>
        </div>
      )}

      {/* Trade Visualizer Modal — nur wenn vizContext vorhanden */}
      {vizContext && vizState.open && (
        <TradeVisualizer
          trade={sorted[vizState.index]}
          context={vizContext}
          allTrades={sorted}
          currentIndex={vizState.index}
          onNavigate={idx => setVizState({ open: true, index: idx })}
          onClose={() => setVizState({ open: false, index: 0 })}
        />
      )}
    </div>
  );
}
